import * as React from 'react'
import { ReactElement, useEffect, useRef, useState } from 'react'
import { Alert, Tree } from '@fluentui/react-components/unstable'
import { InvoiceProps } from './InvoiceForm'
import { BankTransactionDto } from '../../../../config/finance/invoiceConfig'
import { makeStyles, Toolbar, ToolbarButton, ToolbarGroup } from '@fluentui/react-components'
import { AddRegular } from '@fluentui/react-icons'
import { css, Stack, StackItem } from '@fluentui/react'
import { baseListStyles } from '../../../List/BaseList'
import BankTransaction from './BankTransaction'
import BankTransactionDialog from './BankTransaction/Dialog/BankTransactionDialog'
import { DateTime } from 'luxon'
import { useTranslation } from 'react-i18next'

export type BankTransactionsProps = InvoiceProps & {
    bankTransactions: BankTransactionDto[]
}

const useStyles = makeStyles({
    toolbar: {
        justifyContent: 'end',
    },
    treeItem: {
        marginLeft: '-16px',
        marginRight: '-16px',
        '&.is-selected': {
            marginLeft: '-16px',
            marginRight: '-16px',
            paddingLeft: '7px',
            paddingRight: '7px',
        },
    },
})

export default function BankTransactions(props: BankTransactionsProps): ReactElement {
    const { t: translate } = useTranslation()
    const styles = useStyles()

    const [add, setAdd] = useState(false)
    const elements = useRef<HTMLElement[]>([])
    const addCallback = () => {
        props.bankTransactions.push({
            value: '0.00',
            date: DateTime.local().toISODate(),
            description: '',
        })
        setAdd(true)
    }
    const removeCallback = (removeIndex: number) => {
        props.bankTransactions.splice(removeIndex, 1)
    }

    useEffect(() => {
        // Make sure that the refs are always kept updated.
        // Courtesy of https://stackoverflow.com/a/56063129
        if (props.bankTransactions) {
            elements.current = elements.current.slice(0, props.bankTransactions.length)
        }
    }, [props.bankTransactions])

    if (!props.bankTransactions?.length) {
        return (
            <Alert
                intent="info"
                action={
                    props.isFinal && {
                        icon: <AddRegular />,
                        onClick: addCallback,
                        children: translate('action.add'),
                    }
                }
            >
                {translate('invoice.edit.form.banktransactions.empty')}
                {props.isEditable && ' ' + translate('invoice.edit.form.banktransactions.info.draft')}.
            </Alert>
        )
    }

    return (
        <Tree
            size="medium"
            aria-label={translate('invoice.edit.form.banktransactions.label')}
            appearance="transparent"
            className={css(baseListStyles.listContainer)}
        >
            {props.isFinal && (
                <Toolbar size="small" className={styles.toolbar}>
                    <ToolbarGroup>
                        <ToolbarButton icon={<AddRegular />} onClick={addCallback}>
                            {translate('action.add')}
                        </ToolbarButton>
                        {add && (
                            <BankTransactionDialog
                                {...props}
                                key={`dialog-transaction-new-${props.bankTransactions.at(-1).id}`}
                                open={add}
                                setOpen={setAdd}
                                isNew
                                index={props.bankTransactions.length - 1}
                                bankTransaction={props.bankTransactions.at(-1)}
                                remove={removeCallback}
                            />
                        )}
                    </ToolbarGroup>
                </Toolbar>
            )}
            {props.bankTransactions.map((child: BankTransactionDto, index: number) => (
                <div key={index} ref={element => (elements.current[index] = element)}>
                    <Stack
                        tokens={{ childrenGap: 'm' }}
                        horizontal
                        verticalAlign="center"
                        className={css(baseListStyles.itemCell, styles.treeItem)}
                    >
                        <StackItem grow>
                            <BankTransaction
                                {...props}
                                bankTransaction={child}
                                bankTransactions={props.bankTransactions}
                                index={index}
                                remove={removeCallback}
                            />
                        </StackItem>
                    </Stack>
                </div>
            ))}
        </Tree>
    )
}
